/**
 * TierCelebration — configurable confetti burst + "TEXT!" overlay that
 * fires around the border of a pricing tier card when the patient
 * selects it. One component, many presets — Royal (purple, 4 bursts +
 * bell-ding + "ROYAL!"), Bundle (green, 2 bursts, no sound + "SAVE 20%!"),
 * etc.
 *
 * Pure CSS keyframes + Web Audio (graceful no-op on unsupported
 * browsers). GPU-accelerated — only transform + opacity are animated.
 *
 * Props:
 *   - burstKey: any value that changes on each click → restarts the burst
 *   - active:   when true, also renders a slow shimmer ring so the card
 *               stays "alive" while the tier is selected.
 *   - preset:   "royal" (default) | "bundle" | "monthly" — picks colors,
 *               burst count, sound, and label.
 */
import { memo, useEffect, useId } from "react";

const PRESETS = {
  royal: {
    label: "ROYAL!",
    sparks: 14,
    bursts: 4,
    burstInterval: 380,
    colors: ["#c084fc", "#a855f7", "#d8b4fe", "#e9d5ff", "#7c3aed", "#f5d0fe"],
    ring: "rgba(168, 85, 247, 0.9)",
    shimmer1: "rgba(168, 85, 247, 0.55)",
    shimmer2: "rgba(168, 85, 247, 0.35)",
    shimmer3: "rgba(216, 180, 254, 0.95)",
    shimmer4: "rgba(168, 85, 247, 0.6)",
    textGradient: "linear-gradient(135deg, #a855f7 0%, #c084fc 45%, #f0abfc 100%)",
    textStroke: "rgba(168, 85, 247, 0.35)",
    textShadow: "rgba(168, 85, 247, 0.45)",
    fontSize: 44,
    playSound: true,
  },
  bundle: {
    label: "SAVE 20%!",
    sparks: 10,
    bursts: 2,
    burstInterval: 320,
    colors: ["#34d399", "#10b981", "#6ee7b7", "#d1fae5", "#059669", "#a7f3d0"],
    ring: "rgba(16, 185, 129, 0.9)",
    shimmer1: "rgba(16, 185, 129, 0.5)",
    shimmer2: "rgba(16, 185, 129, 0.3)",
    shimmer3: "rgba(110, 231, 183, 0.9)",
    shimmer4: "rgba(16, 185, 129, 0.5)",
    textGradient: "linear-gradient(135deg, #059669 0%, #10b981 45%, #6ee7b7 100%)",
    textStroke: "rgba(16, 185, 129, 0.35)",
    textShadow: "rgba(16, 185, 129, 0.4)",
    fontSize: 32,
    playSound: false,
  },
};

/** Lightweight bell-ding via Web Audio — 3 sine overtones, ~420ms decay. */
function playBellDing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [880, 1320, 1760].forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now);
      const startVol = 0.22 / (idx + 1);
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(startVol, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.42 + idx * 0.05);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.5);
    });
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 700);
  } catch { /* unsupported — graceful no-op */ }
}

function BurstLayer({ delayMs, cfg, ns }) {
  return (
    <span className="absolute inset-0 overflow-visible">
      <span
        className="absolute inset-0 rounded-xl"
        style={{
          animation: `${ns}-ring 0.9s ${delayMs}ms ease-out forwards`,
          border: `2px solid ${cfg.ring}`,
          opacity: 0,
        }}
      />
      {Array.from({ length: cfg.sparks }).map((_, i) => {
        const angle = (i / cfg.sparks) * Math.PI * 2 + delayMs * 0.0007;
        const dist = 120 + Math.random() * 45;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist;
        const colour = cfg.colors[i % cfg.colors.length];
        const size = 5 + Math.random() * 4;
        const dur = 0.85 + Math.random() * 0.4;
        return (
          <span
            key={i}
            className="absolute left-1/2 top-1/2"
            style={{
              width: size,
              height: size,
              marginLeft: -size / 2,
              marginTop: -size / 2,
              background: colour,
              boxShadow: `0 0 ${size * 2}px ${colour}`,
              borderRadius: "50%",
              opacity: 0,
              animation: `${ns}-spark${i % 3} ${dur}s ${delayMs}ms ease-out forwards`,
              ["--dx"]: `${dx}px`,
              ["--dy"]: `${dy}px`,
            }}
          />
        );
      })}
    </span>
  );
}

function TierCelebrationInner({ burstKey, active, preset = "royal", labelOverride }) {
  const cfg = PRESETS[preset] || PRESETS.royal;
  const label = (labelOverride && String(labelOverride).trim()) || cfg.label;
  // Per-instance namespace so the keyframe names don't collide when
  // multiple presets coexist on the same page.
  const reactId = useId();
  const ns = `tcb-${reactId.replace(/[:]/g, "")}`;

  useEffect(() => {
    if (!burstKey || !cfg.playSound) return;
    playBellDing();
  }, [burstKey, cfg.playSound]);

  return (
    <span
      key={burstKey}
      data-testid={`tier-celebration-${preset}`}
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-visible"
    >
      {active && (
        <span
          className="absolute inset-0 rounded-xl"
          style={{ animation: `${ns}-shimmer 2.4s ease-in-out infinite` }}
        />
      )}

      {Array.from({ length: cfg.bursts }).map((_, b) => (
        <BurstLayer key={b} delayMs={b * cfg.burstInterval} cfg={cfg} ns={ns} />
      ))}

      <span
        className="absolute left-1/2 top-1/2 pointer-events-none select-none"
        style={{
          transform: "translate(-50%, -50%)",
          fontFamily: "'Outfit', 'Inter', sans-serif",
          fontWeight: 900,
          fontSize: `${cfg.fontSize}px`,
          letterSpacing: "0.08em",
          background: cfg.textGradient,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
          WebkitTextStroke: `1px ${cfg.textStroke}`,
          textShadow: `0 4px 18px ${cfg.textShadow}, 0 1px 0 rgba(255,255,255,0.7)`,
          opacity: 0,
          animation: `${ns}-text 1.4s 60ms cubic-bezier(.18,.89,.32,1.28) forwards`,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>

      <style>{`
        @keyframes ${ns}-ring {
          0%   { transform: scale(0.92); opacity: 0; filter: blur(0); }
          25%  { opacity: 1; }
          100% { transform: scale(1.22); opacity: 0; filter: blur(2px); }
        }
        @keyframes ${ns}-shimmer {
          0%, 100% {
            box-shadow: 0 0 0 1px ${cfg.shimmer1}, 0 0 18px 2px ${cfg.shimmer2};
          }
          50% {
            box-shadow: 0 0 0 2px ${cfg.shimmer3}, 0 0 30px 6px ${cfg.shimmer4};
          }
        }
        @keyframes ${ns}-spark0 {
          0%   { opacity: 0; transform: translate(0,0) scale(0.4); }
          12%  { opacity: 1; }
          100% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(0.6); }
        }
        @keyframes ${ns}-spark1 {
          0%   { opacity: 0; transform: translate(0,0) scale(0.3); }
          18%  { opacity: 1; transform: translate(calc(var(--dx) * 0.25), calc(var(--dy) * 0.25)) scale(1.2); }
          100% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(0.4); }
        }
        @keyframes ${ns}-spark2 {
          0%   { opacity: 0; transform: translate(0,0) scale(0.5) rotate(0deg); }
          15%  { opacity: 1; }
          100% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(0.3) rotate(220deg); }
        }
        @keyframes ${ns}-text {
          0%   { opacity: 0; transform: translate(-50%, -30%) scale(0.6); filter: blur(8px); }
          18%  { opacity: 1; transform: translate(-50%, -52%) scale(1.18); filter: blur(0); }
          55%  { opacity: 1; transform: translate(-50%, -70%) scale(1.04); }
          100% { opacity: 0; transform: translate(-50%, -120%) scale(0.9); filter: blur(2px); }
        }
      `}</style>
    </span>
  );
}

export default memo(TierCelebrationInner);
